import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SalesDevelopmentUser {
  id: string;
  auth_user_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  active: boolean;
  system_role_id?: string;
  department_id?: string;
}

export const useSalesDevelopment = () => {
  const { data: sdrs, isLoading, error } = useQuery({
    queryKey: ['sales-development-reps'],
    queryFn: async () => {
      // Get the sales development role by exact code
      const { data: salesRole, error: roleError } = await supabase
        .from('system_roles')
        .select('id, code, name')
        .eq('code', 'sales_dev')
        .eq('is_active', true)
        .maybeSingle();

      if (roleError) throw roleError;
      
      if (!salesRole) {
        // If sales_dev role not found, return empty array
        console.warn('Sales Development role (sales_dev) not found');
        return [];
      }

      // Get users with sales development role
      const { data: users, error: usersError } = await supabase
        .from('app_users')
        .select(`
          *,
          system_role:system_roles(id, name, code)
        `)
        .eq('system_role_id', salesRole.id)
        .eq('active', true)
        .order('first_name');

      if (usersError) throw usersError;
      
      // Fetch user identity links for deduplication
      const { data: links, error: linksError } = await supabase
        .from('user_identity_links')
        .select('legacy_user_id, current_user_id');

      if (linksError) throw linksError;

      // Create a map of legacy_user_id -> current_user_id
      const legacyToCurrent = new Map<string, string>();
      (links || []).forEach(link => {
        legacyToCurrent.set(link.legacy_user_id, link.current_user_id);
      });

      // Filter out legacy users (keep only current users)
      const deduplicatedUsers = (users || []).filter(user => {
        // If this user is a legacy user (exists in the map as a key), exclude it
        return !legacyToCurrent.has(user.id);
      });

      // Fallback deduplication by email (for users without identity links)
      const emailToWinner = new Map<string, SalesDevelopmentUser>();
      
      deduplicatedUsers.forEach((user) => {
        if (!user.email) return;
        const normalizedEmail = user.email.trim().toLowerCase();
        
        if (!emailToWinner.has(normalizedEmail)) {
          emailToWinner.set(normalizedEmail, user);
        } else {
          const currentWinner = emailToWinner.get(normalizedEmail)!;
          let shouldReplace = false;
          
          // Priority 1: User referenced as current_user_id in links
          const isCurrentInLinks = (links || []).some(l => l.current_user_id === user.id);
          const winnerIsCurrentInLinks = (links || []).some(l => l.current_user_id === currentWinner.id);
          
          if (isCurrentInLinks && !winnerIsCurrentInLinks) {
            shouldReplace = true;
          } else if (!isCurrentInLinks && !winnerIsCurrentInLinks) {
            // Priority 2: User with auth_user_id
            if (user.auth_user_id && !currentWinner.auth_user_id) {
              shouldReplace = true;
            } else if (user.auth_user_id === currentWinner.auth_user_id) {
              // Priority 3: More complete name fields
              const userNameScore = (user.display_name ? 3 : 0) + (user.first_name ? 1 : 0) + (user.last_name ? 1 : 0);
              const winnerNameScore = (currentWinner.display_name ? 3 : 0) + (currentWinner.first_name ? 1 : 0) + (currentWinner.last_name ? 1 : 0);
              
              if (userNameScore > winnerNameScore) {
                shouldReplace = true;
              }
            }
          }
          
          if (shouldReplace) {
            emailToWinner.set(normalizedEmail, user);
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`[SDR] Deduplicated by email fallback: ${normalizedEmail}`);
            }
          }
        }
      });
      
      // Keep only winners
      const uniqueByEmailUsers = deduplicatedUsers.filter(user => 
        !user.email || emailToWinner.get(user.email.trim().toLowerCase())?.id === user.id
      );

      return uniqueByEmailUsers as SalesDevelopmentUser[];
    },
  });

  return { 
    salesDevelopment: sdrs || [], 
    isLoading, 
    error,
    count: sdrs?.length || 0 
  };
};
